/* eslint-disable react-hooks/exhaustive-deps */

import { Button, Flex, Input, Stack, Text, Textarea } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { fetchAPI } from '@/api/fetch';
import { AnalyzeInterviewResponse, apiAdminAnalyzeInterview } from '@/api/services';
import { useAdminAnalysis } from '@/store/adminAnalysis';
import CandidateSummary from '@/views/Job/Candidate/CandidateSummary';

type FormValues = {
  jobTitle: string;
  jobDescription: string;
  requirements: string;
  niceToHaves: string;
  transcripts: string;
};

export default function InterviewAnalysisSection() {
  const [submitError, setSubmitError] = useState(false);
  const {
    jobDescription,
    jobRequirements,
    resumeAnalysisResult,
    interviewAnalysisResult,
    setInterviewAnalysisResult,
    followUpQuestions,
    setFollowUpQuestions,
    clearInterviewAnalysisResult,
    resume,
    clearAll
  } = useAdminAnalysis();

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>();

  useEffect(() => {
    if (jobDescription) {
      setValue('jobTitle', jobDescription.job_title);
      setValue('jobDescription', jobDescription.job_description);
    }
    if (jobRequirements) {
      setValue('requirements', jobRequirements.requirements.join('\n'));
      setValue('niceToHaves', jobRequirements.nice_to_haves.join('\n'));
    }
  }, []);

  const handleFormSubmit = async (formData: FormValues) => {
    if (!resume || !followUpQuestions) {
      console.error('Missing required data:', {
        resume: !resume ? 'No resume uploaded.' : 'Resume available.',
        followUpQuestions: !followUpQuestions
          ? 'No follow up questions.'
          : 'Follow up questions available.'
      });
      return;
    }

    setSubmitError(false);
    clearInterviewAnalysisResult();

    const parseRequirements = (input: string) =>
      input
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

    const jobRequirements = {
      requirements: parseRequirements(formData.requirements),
      nice_to_haves: parseRequirements(formData.niceToHaves)
    };

    let transcripts;
    try {
      transcripts = JSON.parse(formData.transcripts);
      if (!Array.isArray(transcripts)) {
        setError('transcripts', {
          type: 'manual',
          message: 'Invalid transcripts format'
        });

        return;
      }

      if (transcripts.length === 0) {
        setError('transcripts', {
          type: 'manual',
          message: 'Transcripts cannot be empty'
        });
        return;
      }

      if (
        transcripts.some((item) => typeof item !== 'object' || !item.speaker_name || !item.text)
      ) {
        setError('transcripts', {
          type: 'manual',
          message: 'Transcripts must be an array of objects with speaker_name and text keys'
        });
        return;
      }
    } catch (error) {
      console.error('Failed to parse transcripts:', error);
      setError('transcripts', {
        type: 'manual',
        message: 'Invalid transcripts format'
      });

      return;
    }

    try {
      const response = await fetchAPI<AnalyzeInterviewResponse>({
        request: () =>
          apiAdminAnalyzeInterview({
            payload: {
              job_title: formData.jobTitle,
              job_description: formData.jobDescription,
              job_requirements: jobRequirements,
              alerts_and_follow_up_questions: followUpQuestions,
              aligned_skills: resumeAnalysisResult?.aligned_skills || [],
              gaps_or_misalignments: resumeAnalysisResult?.gaps_or_misalignments || [],
              transcripts
            }
          })
      });

      if (response.success) {
        setInterviewAnalysisResult({
          overview: response.data.overview,
          score: response.data.score,
          contents: response.data.contents,
          aligned_skills: response.data.aligned_skills,
          gaps_or_misalignments: response.data.gaps_or_misalignments
        });
        setFollowUpQuestions(response.data.alerts_and_follow_up_questions);
      } else {
        console.error('Failed to analyze interview:', response.message);
        setSubmitError(true);
      }
    } catch (error) {
      console.error('Failed to analyze interview:', error);
      setSubmitError(true);
    }
  };

  const clear = () => {
    reset();
    clearAll();
  };

  return (
    <Stack gap="24px">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Flex h="100%" gap="24px" alignItems="flex-start">
          <Stack w="100%">
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Job Title
            </Text>
            <Input
              border="1px solid"
              borderColor={errors.jobTitle ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              borderRadius="8px"
              {...register('jobTitle', { required: 'Job title is required.' })}
            />
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Job Description
            </Text>
            <Textarea
              p="16px 24px"
              placeholder="Enter job description here..."
              border="1px solid"
              borderColor={errors.jobDescription ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="120px"
              maxH="120px"
              borderRadius="8px"
              {...register('jobDescription', {
                required: 'Job description is required.'
              })}
            />
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Requirements
            </Text>
            <Textarea
              p="16px 24px"
              placeholder="Enter requirements here..."
              border="1px solid"
              borderColor={errors.requirements ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="120px"
              maxH="120px"
              borderRadius="8px"
              {...register('requirements', {
                required: 'Requirements are required.'
              })}
            />
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Nice to Haves
            </Text>
            <Textarea
              p="16px 24px"
              placeholder="Enter nice to haves here..."
              border="1px solid"
              borderColor={errors.niceToHaves ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="120px"
              maxH="120px"
              borderRadius="8px"
              {...register('niceToHaves', {
                required: 'Nice to haves are required.'
              })}
            />
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Transcripts
            </Text>
            <Textarea
              p="16px 24px"
              placeholder="Enter transcripts here..."
              border="1px solid"
              borderColor={errors.transcripts ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="120px"
              maxH="120px"
              borderRadius="8px"
              {...register('transcripts', {
                required: 'Transcripts are required.'
              })}
            />
            {errors.transcripts && (
              <Text fontSize="14px" color="error.700" mt="5px">
                {errors.transcripts.message}
              </Text>
            )}
            <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
              Resume
            </Text>
            <Text fontSize="16px" color={resume ? 'neutrals.800' : 'error.700'}>
              {resume
                ? `Resume uploaded. [${resume.personal_information.name}]`
                : 'No resume data.'}
            </Text>
          </Stack>
          <Stack gap="20px">
            <Button
              type="submit"
              loading={isSubmitting}
              bg="primary.900"
              fontSize="16px"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
            >
              Analyze
            </Button>
            <Button
              type="button"
              bg="background.secondary"
              color="primary.900"
              fontSize="16px"
              border="1px solid"
              borderColor="primary.900"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
              onClick={clearInterviewAnalysisResult}
            >
              Clear Result
            </Button>
            <Button
              type="button"
              bg="background.secondary"
              color="primary.900"
              fontSize="16px"
              border="1px solid"
              borderColor="primary.900"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
              onClick={clear}
            >
              Clear All
            </Button>
          </Stack>
        </Flex>
        {errors.jobDescription && (
          <Text fontSize="14px" color="error.700" mt="5px">
            {errors.jobDescription.message}
          </Text>
        )}
        {submitError && (
          <Text fontSize="16px" color="error.700" mt="5px">
            Failed to analyze resume. Please try again.
          </Text>
        )}
      </form>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Analyzed Interview
      </Text>
      {interviewAnalysisResult && resumeAnalysisResult ? (
        <Stack gap="12px">
          <Flex alignItems="center">
            <Text fontSize="16px" color="neutrals.700" mr="8px">
              Score:
            </Text>
            <Text fontSize="16px" color="neutrals.800" fontWeight="semibold">
              {interviewAnalysisResult?.score?.score}
            </Text>
          </Flex>
          <CandidateSummary
            resumeAnalysisResult={resumeAnalysisResult}
            overviewAnalysisResult={interviewAnalysisResult}
            followUpQuestions={followUpQuestions || []}
          />
        </Stack>
      ) : (
        <Text fontSize="16px" color="neutrals.700">
          No interview analyzed yet.
        </Text>
      )}
    </Stack>
  );
}
